import { ensureListData } from "@common/api/utils"
import {
  workPackageGroupPages,
  workPackagesInfiniteQuery,
} from "@common/api/work-packages"
import { createFileRoute, Outlet, useNavigate } from "@tanstack/react-router"
import { ActiveFilterBar } from "@/components/layout/active-filter-bar"
import { ColumnChips } from "@/components/layout/column-chips"
import { DisplayOptions } from "@/components/layout/display-options"
import { FilterButton } from "@/components/layout/filter-button"
import { ListPage } from "@/components/layout/list-page"
import { ListTabs } from "@/components/layout/list-tabs"
import { ListToolbar } from "@/components/layout/list-toolbar"
import { NameFilter } from "@/components/layout/name-filter"
import { useColumnSizing } from "@/hooks/use-column-layout"
import { useWidth } from "@/hooks/use-width"
import { fitColumns } from "@/lib/column-widths"
import { requirePermission } from "@/lib/require-permission"
import { CloseStrip } from "../-components/close-strip"
import { NewWorkOrderButton } from "../-components/new-work-order-button"
import { WorkOrderTable } from "../-components/work-order-table"
import {
  COLUMN_LABELS,
  COLUMN_SIZING,
  COLUMNS,
  type Column,
  DEFAULT_COLUMNS,
} from "../-lib/columns"
import {
  hasWorkOrderFilters,
  NO_WORK_ORDER_FILTERS,
  workOrderFilterConfigs,
} from "../-lib/filters"
import { GROUPING_LABELS, GROUPINGS } from "../-lib/grouping"
import {
  columnsSearch,
  displayOf,
  groupingSearch,
  sortSearch,
  TABS,
  tabLabel,
  type WorkOrdersDisplay,
  type WorkOrdersSearch,
  workOrderPagesParams,
  workOrdersSearchSchema,
} from "../-lib/search"

export const Route = createFileRoute("/_layout/work-orders/_list")({
  staticData: { breadcrumb: "Work orders" },
  validateSearch: (search) => workOrdersSearchSchema.parse(search),
  loaderDeps: ({ search }) => ({
    pages: workOrderPagesParams(search),
    grouped: Boolean(displayOf(search).group),
  }),
  beforeLoad: ({ context: { queryClient } }) =>
    requirePermission(queryClient, "work_package:read", "/"),
  loader: async ({ context: { queryClient }, deps }) => {
    await ensureListData({
      grouped: deps.grouped,
      groups: () =>
        queryClient.ensureQueryData(workPackageGroupPages.groups(deps.pages)),
      flat: () =>
        queryClient.ensureInfiniteQueryData(
          workPackagesInfiniteQuery(deps.pages),
        ),
    })
  },
  component: WorkOrdersPage,
})

function useUpdateSearch(): (patch: Partial<WorkOrdersSearch>) => void {
  const navigate = useNavigate()
  return (patch) =>
    navigate({
      to: ".",
      search: (previous) => ({ ...previous, ...patch }),
      replace: true,
    })
}

function WorkOrdersPage(): React.ReactElement {
  const search = Route.useSearch()
  const display = displayOf(search)
  const update = useUpdateSearch()
  const [ref, width] = useWidth<HTMLDivElement>()
  const sizing = useColumnSizing(display.columns, COLUMN_SIZING)
  const fit = fitColumns(display.columns, width, sizing)

  return (
    <ListPage
      toolbar={<Toolbar display={display} hidden={fit.hidden} />}
      strip={<CloseStrip />}
      detail={<Outlet />}
      scrollRef={ref}
    >
      <ActiveFilterBar
        active={hasWorkOrderFilters(search)}
        configs={workOrderFilterConfigs}
        values={search}
        onChange={update}
        onClear={() => update(NO_WORK_ORDER_FILTERS)}
      />
      <WorkOrderTable
        search={search}
        display={display}
        columns={fit.shown}
        onSort={(column) => update(sortSearch(display, column))}
        onClear={() => update({ ...NO_WORK_ORDER_FILTERS, search: undefined })}
      />
    </ListPage>
  )
}

function Toolbar({
  display,
  hidden,
}: {
  display: WorkOrdersDisplay
  hidden: Column[]
}): React.ReactElement {
  const values = Route.useSearch()
  const { tab = "all", search = "" } = values
  const update = useUpdateSearch()

  return (
    <ListToolbar
      tabs={
        <ListTabs
          tabs={TABS}
          tab={tab}
          label={tabLabel}
          onSelect={(next) =>
            update({ tab: next === "all" ? undefined : next })
          }
        />
      }
      actions={
        <>
          <NameFilter
            search={search}
            onSearch={(next) => update({ search: next || undefined })}
            placeholder="Search work orders…"
          />
          <FilterButton
            active={hasWorkOrderFilters(values)}
            configs={workOrderFilterConfigs}
            onChange={update}
          />
          <DisplayOptions
            groupings={GROUPINGS}
            labels={GROUPING_LABELS}
            group={display.group}
            subgroup={display.subgroup}
            onChange={(next) => update(groupingSearch(next))}
          >
            <ColumnChips
              options={COLUMNS}
              labels={COLUMN_LABELS}
              fixed={["number"]}
              columns={display.columns}
              hidden={hidden}
              onChange={(next) => update(columnsSearch(next, DEFAULT_COLUMNS))}
            />
          </DisplayOptions>
          <NewWorkOrderButton>New work order</NewWorkOrderButton>
        </>
      }
    />
  )
}
